import {clamp} from "./mathUtils"

function isWebGL2Supported() {
    try {
        const canvas = document.createElement('canvas');
        return !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
    } catch (e) {
        return false;
    }
}

function showGlobalSpinner() {
    document.getElementById('spacewalk-spinner').style.display = 'block'
}

function hideGlobalSpinner() {
    document.getElementById('spacewalk-spinner').style.display = 'none'
}

// Run an async operation with the global spinner shown, guaranteeing the
// spinner is hidden afterward even if the operation throws. Replaces hand-paired
// showGlobalSpinner()/hideGlobalSpinner() calls that strand the spinner on failure.
async function withSpinner(fn) {
    showGlobalSpinner()
    try {
        return await fn()
    } finally {
        hideGlobalSpinner()
    }
}

function unsetDataMaterialProviderCheckbox(igvPanel) {
    const trackViews = igvPanel.browser?.trackViews ?? [];
    for (const trackView of trackViews) {
        if (trackView.track) {
            trackView.track.embeddingCheckboxChecked = false;
        }
        if (trackView.materialProviderInput) {
            trackView.materialProviderInput.checked = false;
        }
    }
    igvPanel.clearMaterialProviderSessionState();
}

function fitToContainer(canvas, devicePixelRatio) {

    canvas.style.width ='100%';
    canvas.style.height ='100%';

    canvas.width  = devicePixelRatio ? devicePixelRatio * canvas.offsetWidth : canvas.offsetWidth;
    canvas.height = devicePixelRatio ? devicePixelRatio * canvas.offsetHeight : canvas.offsetHeight;
}

function getMouseXY(domElement, { clientX, clientY }) {

    // a DOMRect object with eight properties: left, top, right, bottom, x, y, width, height
    const { left, top, width, height } = domElement.getBoundingClientRect();

    const xNormalized = clamp((clientX - left)/width, 0, 1)
    const yNormalized = clamp((clientY - top)/height, 0, 1)
    return { x: clientX - left,  y: clientY - top, xNormalized, yNormalized };

}

function debounce(func, delay) {

    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };

}

function prettyPrint(number) {

    if (typeof number !== "number") {
        console.error(`${ number } must be a number`)
        return
    }

    const integerPart = Math.trunc(number)
    return integerPart.toLocaleString()
}

async function readFileAsDataURL(blob) {

    const fileReader = new FileReader();

    return new Promise((resolve, reject) => {
        fileReader.onerror = () => {
            fileReader.abort();
            reject(new DOMException("Problem parsing input file."));
        };

        fileReader.onload = () => {
            resolve(fileReader.result);
        };

        fileReader.readAsDataURL(blob);
    });
}

function createImage(imageSource) {

    return new Promise((resolve, reject) => {
        let img = new Image();
        img.addEventListener('load', e => resolve(img));
        img.addEventListener('error', () => { reject(new Error(`Failed to load image's URL: ${imageSource}`)); });
        img.src = imageSource;
    });

}

async function transferRGBAMatrixToLiveMapCanvas(ctx, rgbaMatrix, matrixDimension) {

    const imageData = new ImageData(rgbaMatrix, matrixDimension, matrixDimension)

    const imageBitmap = await createImageBitmap(imageData)

    ctx.transferFromImageBitmap(imageBitmap);

}

function disposeThreeJSGroup(group, scene) {

    // Traverse all children of the group
    group.traverse((child) => {
        if (child.isMesh) {
            // Dispose geometry
            if (child.geometry) {
                child.geometry.dispose();
            }

            // Dispose materials
            if (child.material) {
                if (Array.isArray(child.material)) {
                    // Handle case for array of materials
                    child.material.forEach((material) => {
                        if (material.map) material.map.dispose(); // Dispose textures
                        material.dispose(); // Dispose material itself
                    });
                } else {
                    if (child.material.map) child.material.map.dispose(); // Dispose textures
                    child.material.dispose(); // Dispose material itself
                }
            }
        }
    });

    // Remove the group from the scene
    if (scene) {
        scene.remove(group)
    }

    // Set group and children to null to break references (optional)
    group.children = []
}

function getPositionArrayWithTrace(trace){
    const aggregateVertices = []
    for (const { x, y, z } of trace.map(({ xyz }) => xyz)) {
        aggregateVertices.push(x, y, z)
    }
    return aggregateVertices
}

export {
    isWebGL2Supported,
    showGlobalSpinner,
    hideGlobalSpinner,
    withSpinner,
    unsetDataMaterialProviderCheckbox,
    createImage,
    transferRGBAMatrixToLiveMapCanvas,
    readFileAsDataURL,
    fitToContainer,
    getMouseXY,
    debounce,
    prettyPrint,
    disposeThreeJSGroup,
    getPositionArrayWithTrace
};
